import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { HardDrive } from 'lucide-react';
import { getStorageInfo, StorageInfo } from '../services/api';

interface CommunityPoolProps {
  // Single-line landing render. Default (long-form) is preserved for any
  // future surface that wants the stats grid + progress bar + haiku.
  compact?: boolean;
}

const CommunityPool: React.FC<CommunityPoolProps> = ({ compact = false }) => {
  const [storageInfo, setStorageInfo] = useState<StorageInfo>({
    filesCount: 0,
    usedStorageGB: 0,
    maxStorageGB: 1024,
    updatedAt: new Date().toISOString(),
    totalCount: 0,
    totalVolume: 0,
    countToday: 0,
    volumeToday: 0,
    countYesterday: 0,
    volumeYesterday: 0,
    countLastWeek: 0,
    volumeLastWeek: 0,
    countLastMonth: 0,
    volumeLastMonth: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStorageInfo = async () => {
      try {
        const info = await getStorageInfo();
        setStorageInfo(info);
      } catch (error) {
        console.error('Failed to fetch storage info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStorageInfo();
    const interval = setInterval(fetchStorageInfo, 30000);
    return () => clearInterval(interval);
  }, []);

  const usagePercentage = (storageInfo.usedStorageGB / storageInfo.maxStorageGB) * 100;

  const getStorageHealth = () => {
    if (usagePercentage < 10) return {
      haiku: ['Empty vessel waits', 'For waves of sound to gather', 'Ocean lies before'],
      color: 'from-cyan-500 to-blue-500',
    };
    if (usagePercentage < 20) return {
      haiku: ['First drops fill the well', 'Ripples spread across still depths', 'Room for many more'],
      color: 'from-emerald-500 to-green-500',
    };
    if (usagePercentage < 30) return {
      haiku: ['A drop in ocean', 'Vast expanse of space remains', 'Horizons stretch far'],
      color: 'from-teal-500 to-cyan-500',
    };
    if (usagePercentage < 40) return {
      haiku: ['Gentle winds propel', 'Smooth sailing through open seas', 'Clear skies lie ahead'],
      color: 'from-sky-500 to-blue-500',
    };
    if (usagePercentage < 50) return {
      haiku: ['Halfway through the path', 'Looking back and forward too', 'Balance found midway'],
      color: 'from-blue-500 to-indigo-500',
    };
    if (usagePercentage < 60) return {
      haiku: ['Past the center line', 'Journey continues forward', 'More road than behind'],
      color: 'from-violet-500 to-purple-500',
    };
    if (usagePercentage < 70) return {
      haiku: ['Container fills well', 'Steady stream flows without rush', 'Purpose taking shape'],
      color: 'from-purple-500 to-pink-500',
    };
    if (usagePercentage < 80) return {
      haiku: ['Walls draw closer now', 'Warmth of fullness settles in', 'Cozy corners form'],
      color: 'from-amber-500 to-orange-500',
    };
    if (usagePercentage < 90) return {
      haiku: ['River running low', 'Soon the banks will touch the sky', 'Time to clear the flow'],
      color: 'from-orange-500 to-red-500',
    };
    return {
      haiku: ['Vessel nears the brim', 'One more drop and it spills over', 'Release what weighs down'],
      color: 'from-red-500 to-rose-600',
    };
  };

  if (isLoading) return null;

  const health = getStorageHealth();

  // Compact (landing) — a single restrained line + slim bar.
  // No haiku, no gradient chip, no per-row stat tiles.
  if (compact) {
    return (
      <motion.div
        className="w-full rounded-2xl border px-5 py-4"
        style={{
          backgroundColor: 'var(--background-surface)',
          borderColor: 'var(--border-color)',
        }}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between gap-4 mb-2">
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Free tier runs on a shared 1TB pool —{' '}
            <span style={{ color: 'var(--text-primary)' }}>
              {storageInfo.usedStorageGB.toFixed(1)} GB
            </span>{' '}
            in use right now ({usagePercentage.toFixed(1)}%).
          </p>
        </div>
        <div className="relative h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
          <motion.div
            className="h-full bg-soundwave-500"
            initial={{ width: 0 }}
            whileInView={{ width: `${Math.min(usagePercentage, 100)}%` }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: 'easeOut' }}
          />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="w-full rounded-2xl border p-6 md:p-8 relative overflow-hidden"
      style={{
        backgroundColor: 'var(--background-surface)',
        borderColor: 'var(--border-color)',
      }}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${health.color} flex items-center justify-center shadow-md`}>
            <HardDrive className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
              Community Pool
            </p>
            <p className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
              {storageInfo.usedStorageGB.toFixed(1)} / {storageInfo.maxStorageGB.toFixed(0)} GB
              <span className="ml-2 text-sm font-normal" style={{ color: 'var(--text-tertiary)' }}>
                {usagePercentage.toFixed(1)}% used
              </span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 w-full md:w-auto">
          <div className="text-center px-4 py-2 rounded-2xl" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Last 7 days</p>
            <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
              {(storageInfo.countLastWeek / (1024 * 1024 * 1024)).toFixed(1)} GB
            </p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              {storageInfo.volumeLastWeek.toLocaleString()} uploads
            </p>
          </div>
          <div className="text-center px-4 py-2 rounded-2xl" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Last 30 days</p>
            <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
              {(storageInfo.countLastMonth / (1024 * 1024 * 1024)).toFixed(1)} GB
            </p>
            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              {storageInfo.volumeLastMonth.toLocaleString()} uploads
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="relative h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
          <motion.div
            className={`h-full bg-gradient-to-r ${health.color}`}
            initial={{ width: 0 }}
            whileInView={{ width: `${Math.min(usagePercentage, 100)}%` }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: 'easeOut' }}
          />
        </div>
        <p className="font-serif italic text-xs text-center opacity-60" style={{ color: 'var(--text-secondary)' }}>
          {health.haiku.join(' • ')}
        </p>
      </div>
    </motion.div>
  );
};

export default CommunityPool;
